// src/encounter/frame-v2-patient-identity.tsx
//
// PROD-2743 · frame v2 only (`?ef=frameV2`). Design desk ruling D-68 (6 Oct, from audit FV2-39):
// the patient's identity is on screen at EVERY width — the full name plus two identifiers, the date
// of birth with the age, and the MRN (two identifiers: Joint Commission NPSG.01.01.01; the NHS CUI
// patient banner).
//
// While the chart column's patient card is visible, the card carries them and nothing repeats them.
// When the card is NOT visible — the practitioner collapsed the chart, or the frame collapsed it by
// default below 1384px — this compact line in the workspace header carries them:
//   "Aisha Rahman · 46y · DOB 15 Mar 1980 · MRN 00123"
// Person first, the MRN quiet and last (sheet §1 Identity); the DOB in the D-45 date format; the MRN
// drawn exactly as the chart card draws it (mono meta, text-secondary).
//
// EncounterShellView owns whether the chart is collapsed (it is the frame host) and says so through
// `ChartCardHiddenContext`; this line reads it, so it can sit in the workspace tab strip on every
// workspace tab (Encounter, Patient chart, Timeline) without the page owning the collapse.
import { useContext } from 'react';
import { cn, type PatientHeaderData, type PatientHeaderDemographics } from '@scripsteam/scrips-signal-ds';
import { V2_BODY_EMPHASIS } from '@/shared/ui/design-v2';
import { V2_BODY_QUIET, V2_META } from './lib/design-v2';
import { ChartCardHiddenContext, patientIdentityParts } from './lib/patient-identity';

/** One quiet separator between identity parts (not read aloud twice: the parts read on their own). */
function Dot() {
  return (
    <span aria-hidden="true" className={V2_BODY_QUIET}>
      ·
    </span>
  );
}

/**
 * D-68 · the compact identity line, drawn ONLY while the chart card is not visible. Put it where it
 * stays on screen on every workspace tab: the workspace tab strip's end (EncounterWorkspaceTabStrip
 * `endSlot`).
 */
export function FrameV2PatientIdentity({
  patient,
  demographics,
}: {
  patient: PatientHeaderData;
  demographics?: PatientHeaderDemographics;
}) {
  const hidden = useContext(ChartCardHiddenContext);
  if (!hidden) return null;
  const parts = patientIdentityParts(patient, demographics);
  return (
    // The FULL name always (D-68): never truncated. Each part keeps to one line and the line wraps between
    // parts if the strip is ever too narrow (Codex P2 on the 6 Oct fixes).
    <p data-slot="frame-v2-identity" className="m-0 flex min-w-0 flex-wrap items-baseline justify-end gap-x-2 font-sans">
      <span className={cn(V2_BODY_EMPHASIS, 'break-words')}>{parts.name}</span>
      {parts.age ? (
        <>
          <Dot />
          <span className={cn(V2_BODY_QUIET, 'whitespace-nowrap')}>{parts.age}</span>
        </>
      ) : null}
      {parts.dob ? (
        <>
          <Dot />
          <span className={cn(V2_BODY_QUIET, 'whitespace-nowrap')}>{parts.dob}</span>
        </>
      ) : null}
      {parts.mrn ? (
        <>
          <Dot />
          <span title="Medical record number" className={cn(V2_META, 'whitespace-nowrap font-mono')}>
            {parts.mrn}
          </span>
        </>
      ) : null}
    </p>
  );
}
